import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../components/common/Header/Header";
import Modal from "../../../components/common/modal/modal";
import AddressDetails from "../../../components/newHomePolicy/homeFlowSteps/addressDetails/addressDetails";
import Covers from "../../../components/newHomePolicy/homeFlowSteps/covers/covers";
import HomeDetails from "../../../components/newHomePolicy/homeFlowSteps/homeDetails/homeDetails";
import HomeStartPage from "../../../components/newHomePolicy/homeFlowSteps/startPage/startPage";
import Summary from "../../../components/newHomePolicy/homeFlowSteps/summary/summary";
import useHomeFlow from "../../../hooks/useHomeFlowSimple";
import { resetHomeFlow } from "../../../redux/features/homeFlowSlice";
import { clearHomeFormData } from "../../../redux/features/homeFormSlice";
import { clearHomeFiles } from "../../../utils/homeFilesStorage";
import {
  headerStepperData,
  STEP_NAME_ADDRESS_DETAILS,
  STEP_NAME_COVERS,
  STEP_NAME_HOME_DETAILS,
  STEP_NAME_START,
  STEP_NAME_SUMMARY,
} from "./data";

const NewHomePolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(true);
  const { currentStep, currentSubstep } = useHomeFlow();



  // Clear payment redirect flag on component mount (user returned from payment or started fresh)
  useEffect(() => {
    const wasRedirectingToPayment = sessionStorage.getItem("isRedirectingToPayment");
    if (wasRedirectingToPayment === "true") {
      console.log("🔓 Clearing payment redirect flag - user returned or component mounted");
      sessionStorage.removeItem("isRedirectingToPayment");
    }
  }, []);

  // Clear form data when component unmounts or when user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      dispatch(resetHomeFlow());
      dispatch(clearHomeFormData({}));
      clearHomeFiles();
    };

    const handlePopState = () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset - redirecting to payment");
        return;
      }
      dispatch(resetHomeFlow());
      dispatch(clearHomeFormData({}));
      clearHomeFiles();
    };

    // Add event listeners for browser navigation
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Cleanup function - runs when component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      // CRITICAL: Don't clear flow state if we're redirecting to payment
      // This prevents the brief flash of the start screen before redirect
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on unmount - redirecting to payment");
        return;
      }

      // Clear form data and stored files when component unmounts
      dispatch(resetHomeFlow());
      dispatch(clearHomeFormData({}));
      clearHomeFiles();
    };
  }, [dispatch]);

  // Clear form data when user navigates away from the home policy route
  useEffect(() => {
    const currentPath = location.pathname;

    return () => {
      // Don't clear if we're redirecting to payment
      if (sessionStorage.getItem("isRedirectingToPayment") === "true") {
        console.log("⏭️ Skipping flow reset on route change - redirecting to payment");
        return;
      }

      // Only clear if we're leaving the home policy routes
      if (!currentPath.includes("/home-policy")) {
        dispatch(resetHomeFlow());
        dispatch(clearHomeFormData({}));
      }
    };
  }, [location.pathname, dispatch]);

  const handleCloseModal = () => {
    // Reset home flow and form data when modal is closed
    dispatch(resetHomeFlow());
    dispatch(clearHomeFormData({}));
    // Clear temporarily stored files (CPR front/back)
    clearHomeFiles();
    setShowModal(false);
    navigate("/");
  };

  // Don't hide the header for any step
  const shouldHideHeader = false;

  const PageRenderer = () => {
    switch (currentStep?.name) {
      case STEP_NAME_START:
        return <HomeStartPage onClose={handleCloseModal} />;
      case STEP_NAME_HOME_DETAILS:
        return <HomeDetails />;
      case STEP_NAME_ADDRESS_DETAILS:
        return <AddressDetails />;
      case STEP_NAME_COVERS:
        return <Covers />;
      case STEP_NAME_SUMMARY:
        // Handle substeps for Summary step (review_summary, payment_methods)
        if (currentSubstep === "review_summary") {
          return <Summary />;
        }
        // Default to review summary
        return <Summary />;
      default:
        return <HomeStartPage onClose={handleCloseModal} />;
    }
  };

  const activeStepData = useMemo(() => {
    // Map detailed flow steps to stepper display steps
    let stepperKey = currentStep?.name;

    // Map actual flow steps to stepper display steps
    // Stepper: Start → Home → Address → Covers → Pay

    // HOME_DETAILS maps to itself
    if (currentStep?.name === STEP_NAME_HOME_DETAILS) {
      stepperKey = STEP_NAME_HOME_DETAILS;
    }

    // ADDRESS_DETAILS maps to itself
    if (currentStep?.name === STEP_NAME_ADDRESS_DETAILS) {
      stepperKey = STEP_NAME_ADDRESS_DETAILS;
    }

    // COVERS maps to itself
    if (currentStep?.name === STEP_NAME_COVERS) {
      stepperKey = STEP_NAME_COVERS;
    }

    // STEP_NAME_SUMMARY maps to itself in the stepper
    if (currentStep?.name === STEP_NAME_SUMMARY) {
      stepperKey = STEP_NAME_SUMMARY;
    }

    let activeStep = headerStepperData.find((each) => each.key === stepperKey);
    return activeStep;
  }, [currentStep]);

  return (
    <Modal show={showModal}>
      {!shouldHideHeader && (
        <Header
          isPopup
          hideMobileClose
          onCloseModal={handleCloseModal}
          showStepper={true}
          steps={headerStepperData}
          activeStep={activeStepData}
        />
      )}
      <div
        className={`mainHomeModal relative ${
          shouldHideHeader ? "pt-0" : "pt-5"
        } pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll max767:pt-0`}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={`${currentStep?.name || "start"}-${currentSubstep || ""}`}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            {PageRenderer()}
          </motion.div>
        </AnimatePresence>
        {/* Stable mount points for the sticky footer and mobile back button.
            Kept outside AnimatePresence so they never exit/enter between steps
            and don't sit under any transform ancestor. */}
        <div id="home-footer-portal" />
        <div id="home-back-portal" />
        </div>
    </Modal>
  );
};

export default NewHomePolicy;
